import Blink from '@components/Blink'
import useCurrentSection from '@hooks/useCurrentSection'
import { MainMenuItemLevel2 } from '@hooks/useNavigation'
import usePageMetadata from '@hooks/usePageMetadata'
import { FunctionComponent, useEffect, useState, useCallback } from 'react'
import styled, { css } from 'styled-components'
import ThirdLevelMenu from './ThirdLevelMenu'
import useTrackingUtils from '../../useTrackingUtils'
import AnimatedPlusMinusButton from '@components/Buttons/AnimatedPlusMinusButton'

const ItemWrapper = styled.div`
  ${({
    theme: {
      fonts: { goodOT },
    },
  }) => css`
    height: 40px;
    font-family: ${goodOT};
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    padding: 0 24px 0 60px;
    display: flex;
    flex-direction: row;
    align-items: center;
  `}
`

interface StyledBlinkProps {
  isActive: boolean
}

const StyledItem = styled.button<StyledBlinkProps>`
  ${({
    theme: {
      colors: {
        grey: { grey_100 },
        brandColors: { brand_55 },
      },
      fonts: { goodOT },
    },
    isActive,
  }) => css`
    color: ${isActive ? brand_55 : grey_100};
    background-color: transparent;
    border: 0;
    cursor: pointer;
    padding: 0;
    font-family: ${goodOT};
    font-weight: 400;
    font-size: 16px;
    text-decoration: none;
  `}
`

const StyledBlink = styled(Blink)<StyledBlinkProps>`
  ${({
    theme: {
      colors: {
        grey: { grey_100 },
        brandColors: { brand_55 },
      },
    },
    isActive,
  }) => css`
    color: ${isActive ? brand_55 : grey_100};
    text-decoration: none;
    flex: 1 auto;
  `}
`

const SecondLevelItem: FunctionComponent<MainMenuItemLevel2> = ({
  title,
  link,
  content,
  parentRef,
}) => {
  const { isActive } = useCurrentSection(link.title || '')
  const [isSubmenuVisible, setIsSubMenuVisible] = useState(isActive)
  const pageMetadata = usePageMetadata()

  const { burgerMenuClickTrackingFn } = useTrackingUtils({
    title,
    url: link.href,
  })

  useEffect(() => {
    setIsSubMenuVisible(isActive)
  }, [isActive, pageMetadata])

  const resetParentHeight = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.style.height = `${parentRef.current.scrollHeight}px`
      parentRef.current.removeEventListener('transitionend', resetParentHeight)
      parentRef.current.removeEventListener(
        'transitioncancel',
        resetParentHeight
      )
    }
  }, [parentRef])

  const cleanUpListeners = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.removeEventListener('transitionend', resetParentHeight)
      parentRef.current.removeEventListener(
        'transitioncancel',
        resetParentHeight
      )
    }
  }, [parentRef, resetParentHeight])

  const toggleSubmenu = () => {
    setIsSubMenuVisible((prevValue) => !prevValue)
    if (parentRef.current) {
      // Unset height on the parent element, to let second level menu expand fully
      parentRef.current.style.height = ''
      parentRef.current.addEventListener('transitionend', resetParentHeight)
      parentRef.current.addEventListener('transitioncancel', cleanUpListeners)
    }
  }

  return (
    <>
      <ItemWrapper>
        {!link.href ? (
          <StyledItem
            aria-label={title}
            onClick={toggleSubmenu}
            isActive={isActive}>
            {title}
          </StyledItem>
        ) : (
          <StyledBlink
            {...link}
            isActive={!!isActive}
            onClick={burgerMenuClickTrackingFn}>
            {title}
          </StyledBlink>
        )}
        {content?.length && (
          <AnimatedPlusMinusButton
            onClick={toggleSubmenu}
            isPlus={!isSubmenuVisible}
          />
        )}
      </ItemWrapper>
      <ThirdLevelMenu isVisible={isSubmenuVisible}>{content}</ThirdLevelMenu>
    </>
  )
}

export default SecondLevelItem
